import React, { useMemo, useState } from "react";
import { Paper, Typography, Box } from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Label,
} from "recharts";

const COLORS = {
  // Eval chart
  map50:   "#0072B2", // blue
  map75:   "#6A3D9A", // purple
  map5095: "#E69F00", // orange (AP@[.50:.95])

  // Loss charts (use same 3 colors)
  loss_total: "#E69F00", // orange
  loss_cls:   "#0072B2", // blue
  loss_loc:   "#6A3D9A", // purple
  loss_reg:   "#0072B2", // orange 

  // LR chart (blue)
  lr: "#0072B2",
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

// "1K", "2K", "1.5K", etc.
const stepTickK = (v) => {
  if (v == null) return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n) >= 1000) {
    const k = n / 1000;
    return Number.isInteger(k) ? `${k}K` : `${k.toFixed(1)}K`;
  }
  return String(n);
};

// y-ticks/tooltip values
const valTickPctInt = (v) => (v == null ? "" : String(Math.round(Number(v) * 100)));

const valTickLoss = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "0";
  if (Math.abs(n) < 0.01) return n.toFixed(4);
  if (Math.abs(n) < 1)    return n.toFixed(3);
  return n.toFixed(2);
};

const valTickLR = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toExponential(1);
};
const valTickReg = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "0";
  if (n < 0.001) return n.toExponential(1);
  if (n < 0.1)   return n.toFixed(3);
  return n.toFixed(2);
};

const labelFmtK = (p) => (p?.label == null ? "" : `step ${stepTickK(p.label)}`);

// Legend that dedupes series (avoids duplicate-key warnings)
const CustomLegend = ({ payload = [], step }) => {
  const items = useMemo(() => {
    const seen = new Set();
    const out = [];
    payload.forEach((p) => {
      const k = String(p.dataKey ?? p.value ?? out.length);
      if (seen.has(k)) return;
      seen.add(k);
      out.push(p);
    });
    return out;
  }, [payload]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        fontSize: 12,
        mb: 0.5,
      }}
    >
      {step != null && (
        <Typography variant="body2" sx={{ mr: 1 }}>
          step {stepTickK(step)}
        </Typography>
      )}
      {items.map((entry, idx) => (
        <Box key={`${entry.dataKey || entry.value || "k"}-${idx}`} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <span
            style={{
              width: 10,
              height: 10,
              background: entry.color,
              display: "inline-block",
              borderRadius: 2,
            }}
          />
          <span>{entry.value}</span>
        </Box>
      ))}
    </Box>
  );
};

const TrainAndEvalPlots = ({
  metrics,
  heightEval = 300,
  heightLoss = 300,
  heightLR = 300,
  heightReg = 300,
  titleEval = "mAP @0.50, @0.75 & AP@[.50:.95]",
  titleLoss = "Training Losses",
  titleReg = "Regularization Loss",
  titleLR = "Learning Rate",
}) => {
  // ===== Eval mAP series =====
  const dataEval = useMemo(() => {
    if (metrics?.eval && typeof metrics.eval === "object") {
      const rows = Object.entries(metrics.eval).map(([k, row]) => ({
        step: toNum(k),
        map50:   toNum(row["DetectionBoxes_Precision/mAP@.50IOU"]),
        map75:   toNum(row["DetectionBoxes_Precision/mAP@.75IOU"]),
        map5095: toNum(row["DetectionBoxes_Precision/mAP"]), // AP@[.50:.95]
      }));
      return rows
        .filter((r) => Number.isFinite(r.step) && (r.map50 != null || r.map75 != null || r.map5095 != null))
        .sort((a, b) => a.step - b.step);
    }
    const s = metrics?.scalars || {};
    if (s.ap_iou_50 != null || s.map_50 != null || s.ap_iou_75 != null || s.map_75 != null || s.ap_iou_50_95 != null || s.map != null) {
      return [{
        step: 0,
        map50:   toNum(s.ap_iou_50 ?? s.map_50),
        map75:   toNum(s.ap_iou_75 ?? s.map_75),
        map5095: toNum(s.ap_iou_50_95 ?? s.map),
      }];
    }
    return [];
  }, [metrics]);

  const latestStep = dataEval.length ? dataEval[dataEval.length - 1].step : null;

  // ===== Training loss (total/cls/loc) =====
  const dataLoss = useMemo(() => {
    if (metrics?.train && typeof metrics.train === "object") {
      const rows = Object.entries(metrics.train).map(([k, row]) => ({
        step: toNum(k),
        loss_total: toNum(row["Loss/total_loss"]),
        loss_cls:   toNum(row["Loss/classification_loss"]),
        loss_loc:   toNum(row["Loss/localization_loss"]),
      }));
      return rows.filter((r) => Number.isFinite(r.step)).sort((a, b) => a.step - b.step);
    }
    return [];
  }, [metrics]);

  const lossMax = useMemo(() => {
    let max = 0;
    for (const r of dataLoss) {
      ["loss_total", "loss_cls", "loss_loc"].forEach((k) => {
        if (r[k] != null && r[k] > max) max = r[k];
      });
    }
    return max > 0 ? max * 1.1 : 1;
  }, [dataLoss]);

  // ===== Regularization loss (own plot & scale) =====
  const dataRegLoss = useMemo(() => {
    if (metrics?.train && typeof metrics.train === "object") {
      const rows = Object.entries(metrics.train).map(([k, row]) => ({
        step: toNum(k),
        loss_reg: toNum(row["Loss/regularization_loss"]),
      }));
      return rows
        .filter((r) => Number.isFinite(r.step) && r.loss_reg != null)
        .sort((a, b) => a.step - b.step);
    }
    return [];
  }, [metrics]);

  const regMax = useMemo(() => {
    let max = 0;
    for (const r of dataRegLoss) {
      if (r.loss_reg != null && r.loss_reg > max) max = r.loss_reg;
    }
    return max > 0 ? max * 1.25 : 0.1;
  }, [dataRegLoss]);

  // ===== Learning rate =====
  const dataLR = useMemo(() => {
    if (metrics?.train && typeof metrics.train === "object") {
      const rows = Object.entries(metrics.train).map(([k, row]) => ({
        step: toNum(k),
        lr:   toNum(row["learning_rate"]),
      }));
      return rows.filter((r) => Number.isFinite(r.step) && r.lr != null).sort((a, b) => a.step - b.step);
    }
    return [];
  }, [metrics]);

  const [hoverStep, setHoverStep] = useState(null);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderLeft: 4,
        borderColor: "primary.light",
        backgroundColor: (t) =>
          t.palette.mode === "light" ? t.palette.action.hover : "rgba(255,255,255,0.04)",
      }}
    >
      {/* ===== Eval mAP chart ===== */}
      <Typography variant="subtitle1" align="center" sx={{ mb: 1 }}>
        {titleEval} {latestStep != null ? `(latest: ${stepTickK(latestStep)})` : ""}
      </Typography>

      {dataEval.length ? (
        <Box sx={{ width: "100%", height: heightEval }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={dataEval}
              syncId="train_eval_sync"
              onMouseMove={(s) => setHoverStep(s && s.activeLabel != null ? s.activeLabel : null)}
              onMouseLeave={() => setHoverStep(null)}
              margin={{ top: 10, right: 16, left: 12, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="step" type="number" domain={["auto", "auto"]} tickFormatter={stepTickK} tick={{ fontSize: 11 }}>
                <Label value="Steps" position="insideBottom" offset={-4} />
              </XAxis>
              <YAxis domain={[0, 1]} tickFormatter={valTickPctInt} tick={{ fontSize: 11 }}>
                <Label value="mAP (0–100)" angle={-90} position="insideLeft" offset={8} style={{ textAnchor: "middle" }} />
              </YAxis>
              <Legend verticalAlign="top" align="center" content={(props) => <CustomLegend {...props} step={hoverStep} />} />
              <Tooltip
                labelFormatter={labelFmtK}
                formatter={(value, _name, { dataKey }) => [
                  valTickPctInt(value),
                  dataKey === "map50" ? "mAP @0.50" : dataKey === "map75" ? "mAP @0.75" : "AP @[.50:.95]",
                ]}
              />
              <Line type="monotone" dataKey="map50"   name="mAP @0.50"     stroke={COLORS.map50}   strokeWidth={2.5} dot={false} connectNulls />
              <Line type="monotone" dataKey="map75"   name="mAP @0.75"     stroke={COLORS.map75}   strokeWidth={2.5} dot={false} connectNulls />
              <Line type="monotone" dataKey="map5095" name="AP @[.50:.95]" stroke={COLORS.map5095} strokeWidth={2.5} dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ my: 1 }}>
          No mAP points found.
        </Typography>
      )}

      {/* ===== Training Loss chart ===== */}
      <Typography variant="subtitle1" align="center" sx={{ mt: 6, mb: 1 }}>
        {titleLoss}
      </Typography>

      {dataLoss.length ? (
        <Box sx={{ width: "100%", height: heightLoss }}>
          <ResponsiveContainer>
            <LineChart
              data={dataLoss}
              syncId="train_eval_sync"
              onMouseMove={(s) => setHoverStep(s && s.activeLabel != null ? s.activeLabel : null)}
              onMouseLeave={() => setHoverStep(null)}
              margin={{ top: 10, right: 16, left: 12, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="step" type="number" domain={["auto", "auto"]} tickFormatter={stepTickK} tick={{ fontSize: 11 }}>
                <Label value="Steps" position="insideBottom" offset={-4} />
              </XAxis>
              <YAxis domain={[0, lossMax]} tickFormatter={valTickLoss} tick={{ fontSize: 11 }}>
                <Label value="Loss (rounded)" angle={-90} position="insideLeft" offset={8} style={{ textAnchor: "middle" }} />
              </YAxis>
              <Legend verticalAlign="top" align="center" content={(props) => <CustomLegend {...props} step={hoverStep} />} />
              <Tooltip labelFormatter={labelFmtK} formatter={(value, name) => [valTickLoss(value), name]} />
              <Line type="monotone" dataKey="loss_total" name="Loss (total)" stroke={COLORS.loss_total} strokeWidth={2.5} dot={{ r: 2 }} connectNulls />
              <Line type="monotone" dataKey="loss_cls"   name="Loss (cls)"   stroke={COLORS.loss_cls}   strokeWidth={2.5} dot={{ r: 2 }} connectNulls />
              <Line type="monotone" dataKey="loss_loc"   name="Loss (loc)"   stroke={COLORS.loss_loc}   strokeWidth={2.5} dot={{ r: 2 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ my: 1 }}>
          No training loss points found.
        </Typography>
      )}

      {/* ===== Regularization Loss chart (separate scale) ===== */}
      {dataRegLoss.length ? (
        <>
          <Typography variant="subtitle1" align="center" sx={{ mt: 6, mb: 1 }}>
            {titleReg}
          </Typography>
          <Box sx={{ width: "100%", height: heightReg }}>
            <ResponsiveContainer>
              <LineChart
                data={dataRegLoss}
                syncId="train_eval_sync"
                onMouseMove={(s) => setHoverStep(s && s.activeLabel != null ? s.activeLabel : null)}
                onMouseLeave={() => setHoverStep(null)}
                margin={{ top: 10, right: 16, left: 12, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="step" type="number" domain={["auto", "auto"]} tickFormatter={stepTickK} tick={{ fontSize: 11 }}>
                  <Label value="Steps" position="insideBottom" offset={-4} />
                </XAxis>
                <YAxis domain={[0, regMax]} tickFormatter={valTickReg} tick={{ fontSize: 11 }}>
                  <Label value="Regularization Loss" angle={-90} position="insideLeft" offset={8} style={{ textAnchor: "middle" }} />
                </YAxis>
                <Legend verticalAlign="top" align="center" content={(props) => <CustomLegend {...props} step={hoverStep} />} />
                <Tooltip labelFormatter={labelFmtK} formatter={(value) => [valTickReg(value), "Loss (reg)"]} />
                <Line
                  type="monotone"
                  dataKey="loss_reg"
                  name="Loss (reg)"
                  stroke={COLORS.loss_reg}
                  dot={false}
                  connectNulls
                  strokeWidth={2.5}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </>
      ) : null}

      {/* ===== Learning Rate chart (separate) ===== */}
      {dataLR.length ? (
        <>
          <Typography variant="subtitle1" align="center" sx={{ mt: 6, mb: 1 }}>
            {titleLR}
          </Typography>
          <Box sx={{ width: "100%", height: heightLR }}>
            <ResponsiveContainer>
              <LineChart
                data={dataLR}
                syncId="train_eval_sync"
                onMouseMove={(s) => setHoverStep(s && s.activeLabel != null ? s.activeLabel : null)}
                onMouseLeave={() => setHoverStep(null)}
                margin={{ top: 10, right: 16, left: 12, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="step" type="number" domain={["auto", "auto"]} tickFormatter={stepTickK} tick={{ fontSize: 11 }}>
                  <Label value="Steps" position="insideBottom" offset={-4} />
                </XAxis>
                <YAxis tickFormatter={valTickLR} tick={{ fontSize: 11 }}>
                  <Label value="Learning Rate" angle={-90} position="insideLeft" offset={8} style={{ textAnchor: "middle" }} />
                </YAxis>
                <Legend verticalAlign="top" align="center" content={(props) => <CustomLegend {...props} step={hoverStep} />} />
                <Tooltip labelFormatter={labelFmtK} formatter={(value) => [valTickLR(value), "Learning rate"]} />
                <Line type="monotone" dataKey="lr" name="Learning rate" stroke={COLORS.lr} strokeWidth={2.5} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </>
      ) : null}
    </Paper>
  );
};

export default TrainAndEvalPlots;